<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
	isLoginModalOpen: Boolean,
	displayAlert: {
		type: Function,
		required: true
	},
	login: {
		type: Function,
		required: true
	},
	tagEvent: {
		type: Function,
		required: true
	},
	toggleModals: {
		type: Function,
		required: true
	},
	email: "",
	password: "",
	errorMessage: "",
	signup: false,
	forgot: false,
});

function showForgot() {
  props.errorMessage = '';
  props.forgot = true;
}

function backToMain() {
  props.errorMessage = '';
  props.forgot = false;
}

function signUpPrompt() {
	props.signup = true;
}

function submitForm() {
	if (props.forgot) forgotPassword();
	if (props.signup) sendData(true)
	else sendData()
}

async function sendData(newUser = false) {
	props.errorMessage = ""; // Clear previous errors
	const endPoint = "/api/auth/registration";
	const regData = {
		service: 'study',
		app: 'study',
		email: props.email,
		password: props.password,
		signup: !!newUser
	}

	try {
		const serverResponse = await fetch(endPoint, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(regData),
		});

		const responseData = await serverResponse.json();
		console.log(responseData);

		if (responseData.status === 400 || responseData.statusText === "Bad Request" || responseData.success === false) {
			// invalid credentials / user not found
			// TODO: Prompt user to sign up instead.
			console.log('sign up?')
			signUpPrompt();
			return;
		}
		else if (!serverResponse.ok) {
			// if (error = no user) { prompt to sign-up }
			// /api/auth/signup
			throw new Error(responseData.error || "Login failed");
		}

		console.log("Login successful:", responseData);
		props.login(responseData.user);

		// alert user
		var messageText = newUser ? 'Account Created' : 'Signed In';
		props.displayAlert('', messageText, 'toast');

		// log event
		let signUpVal = newUser ? "sign_up" : "sign_in";
		props.tagEvent({where: 'authentication', what: signUpVal});

		closeModal();
		resetModal();
	} catch (error) {
		props.errorMessage = error.message;
		console.error("Login error:", error);
	}
}

async function forgotPassword() {
	props.errorMessage = ""; // Clear previous errors

	try {
		const response = await fetch("/api/auth/forgot", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				email: props.email,
				returnUrl: 'designexternship.com',
    		service: 'study'
			}),
		});

		const data = await response.json();
		console.log(response);

		if (!response.ok) {
			// if (error = no user) { prompt to sign-up }
			// /api/auth/signup
			throw new Error(data.error || `Something went wrong (error ${response.status})`);
		}
		// success message
		props.displayAlert("Message Sent", ["Please check your email.", "If the message doesn't arrive within a few minutes, you can try again or contact us at hello@brownjuice.co"], "", "Okay");
		props.forgot = false;
		closeModal();

	} catch (error) {
		props.errorMessage = error.message;
		console.error("Server returned error:", error);
	}
}

function onDialogMouseDown(event) {
	// Check if click is on the dialog backdrop (not inside the modal content)
	if (event.target === event.currentTarget) {
		closeModal();
	}
}

function closeModal() {
	const dialog = document.getElementById("auth-login");
	dialog.close();
	dialog.setAttribute('data-open', false);
	props.toggleModals()
}

function resetModal() {
	props.email = '';
	props.password = '';
	props.signup = false;
}

/* TODO:
	when the user presses enter
	if (both fields are filled) { submit the form }
*/
</script>

<template>
<dialog
	id="auth-login"
	@mousedown="onDialogMouseDown"
	:data-open="props.isLoginModalOpen"
>
	<main class="left">
		<header>
			<div v-if="props.signup">
				<h2 class="my-0">Create a new account?</h2>
				<p>We won&#8217;t sell your info or spam you.</p>
			</div>
			<div v-if="props.forgot">
				<h2 class="my-0">Forgot your password?</h2>
				<p>No worries! Enter your email address and we'll send you a link.</p>
			</div>
			<div v-if="!props.signup && !props.forgot">
				<h2 class="my-0">Save Your Progress <span class="text-highlight">Free</span><span class="emoji bg-icon">✨</span></h2>
				<p>We won&#8217;t sell your info or spam you.</p>
			</div>
		</header>
		<form id="register" @submit.prevent="submitForm()" method="dialog">
			<label>
				<span>Email:</span>
				<input type="email" name="email" v-model="props.email" autocomplete="username" required />
			</label>
			<label v-if="!props.forgot">
				<div class="flex-row">
					<span>Password:</span>
					<a v-if="!props.signup" @click="showForgot" class="link">Forgot?</a>
				</div>
				<input name="password" v-model="props.password" type="password" :autocomplete="props.signup ? 'new-password' : 'current-password'" required />
			</label>
			<p v-if="props.errorMessage" style="color: var(--text-warning)">{{ props.errorMessage }}</p>
			<p v-if="props.signup" class="error my-0" style="font-size:18px"><em>We couldn&#8217;t find a match for the credentials you entered.</em></p>
			<div v-if="props.signup" class="inline-buttons">
				<a @click="props.signup = !props.signup" class="btn secondary">Back</a>
				<button type="submit" class="btn primary create wide my-0">Create Account</button>
			</div>
			<div v-if="props.forgot">
				<button type="submit" class="btn primary wide my-0">Send Reset Email</button>
				<a @click="backToMain" class="btn wide secondary">Back</a>
			</div>
			<div v-if="!props.signup && !props.forgot">
				<button type="submit" class="btn primary wide">Go</button>
				<a @click="closeModal()" class="btn secondary wide">Cancel</a>
			</div>
		</form>
		<p>Use of this service is governed by our <a href="/study/?page=terms">Terms of Service</a> and <a href="/study/?page=privacy">Privacy Policy</a> (<em>even if you don&#8217;t read them</em>).</p>
	</main>
	<aside class="right">
		<h3>Guidelines to protect you &mdash; and us</h3>
		<p>The gist of our <a href="/study/?page=terms">ToS</a> is <em>&#8220;Use the service in the way we intended&#8221;</em>.</p>
		<ol>
			<li>Don&#8217;t use our service for anything illegal</li>
			<li>Don&#8217;t try to gain access to other people&#8217;s accounts</li>
			<li>Don&#8217;t do anything that could compromise our software, brand, or reputation</li>
		</ol>
		<h3>Privacy is important</h3>
		<details class="accordion">
			<summary><span>We collect anonymous &#8216;usage data&#8217;</span></summary>
			<p>Anonymous details includes things like &#8220;which articles are &#8216;liked&#8217; the most?&#8221;, &#8220;which countries do our users come from?&#8221;, &#8220;do our users use mobile, tablet, or desktop views the most?&#8221;, and &#8220;what percentage of our users are designers, engineers, or other roles?&#8221;.</p>
			<p>Collecting this information is useful for us, because it helps us figure out where to focus our effort and what content to try to improve.</p>
			<p>By definition, &#8216;anonymous data&#8217; does not relate to any specific person, and no matter how you study or compare it, can not be used to identify any individuals or their usage of the service.</p>
		</details>
		<details class="accordion">
			<summary><span>We will never <strong>sell</strong> any &#8220;Personally Identifiable Information&#8221; (&#8216;PII&#8217;)</span></summary>
			<p>We do not have any immediate plans to sell any data, but if we ever do sell data about our service, it will be details like "which articles are more interesting for engineers vs designers?" (in general).</p>
			<p>We will never sell any data <strong>or any combination of data</strong> that could lead to specific users being identified.</p>
		</details>
		<details class="accordion">
			<summary><span>We only <strong>share</strong> your &#8216;PII&#8217; if you ask us to (or when required by law)</span></summary>
			<h4>Regarding the phrasing &#8220;if you ask us to&#8221;</h4>
			<blockquote>
				<p>If you apply for a job through our <a href="/study/?page=jobs">Jobs Board</a>, we will share your profile with the recruiter who posted the job.</p>
				<p>We may also add features where users can interact with each other directly (such as a "design community", "critique space", or messaging services).</p>
				<p>If those features are capable of showing user &#8220;profile&#8221; data, your profile will be set as &#8216;Private&#8217; by default.</p>
				<p>If you choose to engage with those "community" features, we will prompt you to choose which data to share &#8216;publicly&#8217;.</p>
				<p>It should go without saying that &#8220;any data that you choose to &#8216;Post&#8217; to those services also qualifies as you &#8216;asking us&#8217; to share that data&#8220;. It will be up to you to ensure that the data you share does not expose any information you would prefer to keep private.</p>
				<p>It should also go without saying that sharing other people&#8217;s personal information (aka &#8220;Doxing&#8221;) is not allowed <span class="emoji">🚫</span>. (If they want to share their information, that&#8217;s up to them).</p>
			</blockquote>
			<h4>Regarding government requests</h4>
			<blockquote>
				<p>We will comply with government requests when required. We trust that requests will be made in good faith, but that's not our place to judge.</p>
				<p>We will <strong>not</strong> release information about any user who is not a legal resident and under the jurisdiction of the government issuing the request.</p>
				<p>We hope we will never receive a government-request pertaining to any user, but (<em>unless it's forbidden by law in the country where you reside</em>) we will do our best to notify you immediately if we receive a request pertaining to you. If we eventually fulfill that request, we will notify you again.</p>
			</blockquote>
		</details>
		<p>Learn more by reading our <a href="/study/?page=privacy">Privacy Policy</a>.</p>
	</aside>
</dialog>
</template>

<style lang="scss" scoped>
.btn.primary.create {
	background-color: #00a939;
	background-image: linear-gradient(to bottom, #00a939, #2c7344);
}
#auth-login {
	border: none;
	background-color: var(--bg1);
	color: var(--text1);
	display: block;
	margin: 0;
	overflow: auto;
	padding: 0;
	max-width: 100vw;
	max-height: -webkit-fill-available;
	@media(prefers-color-scheme:dark) {
		background-color: var(--bg3);
	}
}
#auth-login > * {
	padding: 24px;
	min-height: fit-content;
}
#auth-login ol {
	padding-left: 1.4rem;
	margin-top: 0;
}
#auth-login li + li { margin-top: 0.25rem; }
#auth-login details { border-bottom: 1px solid var(--border); }
#auth-login blockquote {
	border-left: 1px solid var(--border);
	margin: 0;
	padding-left: .5em;
}

/* FORM STYLES */
#auth-login .left {
	display: flex;
	flex-grow: 0;
	flex-flow: column;
	justify-content: flex-start;
	outline: none!important;
}
#auth-login .left h2 { position: relative; }
#auth-login .left h2 span { font-size: 1rem; }
#auth-login .left span.bg-icon {
	font-size: 1.3rem;
	position: absolute;
	top: 0;
	right: 0;
	z-index: -1;
}
#auth-login h2 + p { margin-top: 4px; }

#auth-login form label {
	display: block;
	margin-bottom: 12px;
}
#auth-login form label span {
	display: block;
	margin-bottom: 8px;
}
#auth-login form .inline-buttons { margin-top: 16px; }
#auth-login form ~ p { margin: 1rem 0 0!important; }

#auth-login .flex-row {
	display: flex;
	flex-flow: row;
	justify-content: space-between;
}

/* INFO COLUMN */
#auth-login .right {
	background-color: var(--bg2);
	border-left: 1px solid var(--border);
	flex-grow: 1;
	min-height: fit-content;
}
#auth-login .right h3 {
	margin-top: 1.7rem;
	margin-bottom: 0;
}
#auth-login .right h3:first-child { margin-top: 0.36rem; }
#auth-login .right h3:first-child + p { margin: 0.4rem 0 0.8rem; }

#auth-login .right > p:last-child { margin: 1rem 0 0!important; }

@media(min-width:768px) {
	#auth-login {
		display: flex;
		flex-flow: row;
		overflow: hidden;
	}
	#auth-login .left,
	#auth-login .right {
		flex-grow: 0;
		width: 50vw;
		height: calc(100vh - 48px); // fallback
		height: calc(100dvh - 48px);
		overflow: auto;
		min-height: unset;
	}
}
@media(min-width:768px) AND (max-height:449px) {
	#auth-login .left,
	#auth-login .right { max-width: 50vw; }
}
@media(min-width:768px) AND (min-height:480px) {
	#auth-login {
		border: 1px solid var(--border);
		border-radius: 24px;
		box-shadow: 0 30px 200px 40px rgba(0, 0, 0, 0.15), 0 30px 50px -30px rgba(0, 0, 0, 0.4), 0 30px 80px -50px rgba(0, 0, 0, 0.5);
		min-height: unset;
		max-height: 454px;
		margin: calc((100vh - 432px) / 2) auto; // fallback
		margin: calc((100dvh - 432px) / 2) auto;
	}
	#auth-login .left,
	#auth-login .right {
		width: unset;
		height: unset;
		min-height: unset;
		max-height: 432px;
	}
	#auth-login .left { max-width: 292px; }
	#auth-login .right { max-width: 376px; }
	#auth-login summary { align-items: flex-start; }
}
</style>
